import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import { readCookie, setCookie } from '../cookies'
import isCypress, { CYPRESS_QA, getCypressEnv } from './isCypress'
import isDevelopment from './isDevelopment'

export const DEFAULT_API_DOMAIN = 'https://apix.purecars.com'
export const QA_API_DOMAIN = 'https://apix-qa.purecars.com'
export const LEGACY_API_DOMAIN = 'https://api.purecars.com'
export const LEGACY_API_DOMAIN_QA = 'https://api-qa.purecars.com'
const API_DOMAIN_COOKIE = 'signal-dev-apix-domain'

const GEO_CDN_DOMAIN = 'https://geo-cdn.purecars.com'
const GEO_CDN_DOMAIN_QA = 'https://geo-cdn-qa.purecars.com'
const GEO_CDN_PROXY_ALIAS = 'geo-cdn'
const LEGACY_API_PROXY_ALIAS = 'legacy'

// this will be replaced by the build server with actual environment information but only in the on-site build
// TODO: provide the ability to use this as the source of truth for hub apps as well
const ON_SITE_ENV = 'ENVIRONMENT_CONFIG_GOES_HERE'

/**
 * gets the API domain to use (defaults to https://apix.purecars.com), but can be overridden through cookies
 * @param {String} middleProxy - the middle part of the apix URL, which is currently reverse proxied and not truly
 *   in existence on developer machines.  If an override is an object mapping these middle proxies we will use it
 * @return {String} - the API domain to use
 */
export function getAPIDomain(middleProxy = '') {
  let defaultApiDomain = DEFAULT_API_DOMAIN
  /* webpack-strip-block:removed */
  const env = isObject(ON_SITE_ENV) ? ON_SITE_ENV : window.env || {}

  // supply QA domain when running locally on cypress
  /* webpack-strip-block:removed */
    // handle the legacy meridian case to default to apix-qa on apps-qa.
    if (env.envName && env.envName !== 'prod') {
      defaultApiDomain = QA_API_DOMAIN
      /* webpack-strip-block:removed */
  }

  let apiRoot = env.apiRoot || defaultApiDomain
  if (!apiRoot.includes('://')) {
    apiRoot = `https://${apiRoot}`
  }

  /* webpack-strip-block:removed */

  return `${apiRoot}${middleProxy}`
}

/**
 * returns the domain to use for legacy API fetches
 * @return {String} - the domain to use for legacy API fetches
 */
export function getLegacyAPIDomain() {
  return getAPIDomain(LEGACY_API_PROXY_ALIAS)
}

/**
 * returns the domain to use for geo cdn fetches
 * @return {String} - the domain to use for geo cdn fetches
 */
export function getGeoCDNDomain() {
  return getAPIDomain(GEO_CDN_PROXY_ALIAS)
}

/**
 * applies the debug API override cookie to environment base URLs
 * @param {String} envKey - the key in process.env in which the base URL is found
 * @return {String} - the base URL to use, with appropriate API domain
 */
export function applyAPIDomainToEnvBase(envKey) {
  const envBase = process.env[envKey]
  return isString(envBase)
    ? envBase.replace(DEFAULT_API_DOMAIN, getAPIDomain())
    : envBase
}

/**
 * developers can use this to reset their API domain override
 */
function resetSignalDevAPIDomain() {
  setCookie({ name: API_DOMAIN_COOKIE, value: '' })
}

/**
 * developers can use this to set their API domain override
 * @param {String|Object} details - the details to set for override.
 *   This can either be a string (the URL to use for all apix requests)
 *   or it can be an object indicating which middle proxies should be handled by which URLs)
 *
 *   Examples:
 *
 *   setSignalDevAPIDomain('https://localhost:4999')
 *
 *   setSignalDevAPIDomain({
 *     '/menu/service': 'https://localhost:4999',
 *     default: 'https://apix-qa.purecars.com'
 *   })
 *
 *   Note that if you use the object form for the details, then the URL that will be hit for a given middle proxy key
 *   will not include that middle proxy segment of the URL.
 *
 *   For example,
 *
 *   setSignalDevAPIDomain({
 *     '/menu/service': 'https://localhost:4999',
 *     default: 'https://apix-qa.purecars.com'
 *   })
 *
 *   the above function will direct requests
 *   from https://apix.purecars.com/menu/service/v1/{accountId} to https://localhost:4999/v1/{accountId}
 *     (no middle proxy segment in URL because there was a specific override for this middle proxy key)
 *   from https://apix.purecars.com/reports/dashboard/v1/foo to https://apix-qa.purecars.com/reports/dashboard/v1/foo
 *     (middle proxy /reports/dashboard was included in URL because this went to the default override)
 */
function setSignalDevAPIDomain(details) {
  setCookie({
    name: API_DOMAIN_COOKIE,
    value: isString(details) ? details : JSON.stringify(details)
  })
}

/**
 * Check to see if in QA environment
 * @return {Boolean} - if the environment is in QA
 */
export function isQAEnvironment() {
  return getAPIDomain().includes('-qa')
}

/* webpack-strip-block:removed */

/**
 * Returns clients domain
 * @return {String} - Client QA or PROD domain
 */
export function getAppsDomain() {
  return isQAEnvironment()
    ? 'https://apps-qa.purecars.com'
    : 'https://apps.purecars.com'
}

/**
 * Returns assets domain
 * @return {String} - Asset QA or PROD domain
 */
export function getAssetsDomain() {
  return isQAEnvironment()
    ? 'https://assets-qa.purecars.com'
    : 'https://assets.purecars.com'
}

/**
 * Returns clients domain
 * @return {String} - Client QA or PROD domain
 */
export function getClientDomain() {
  return isQAEnvironment()
    ? 'https://clients-qa.purecars.com'
    : 'https://clients.purecars.com'
}

export default getAPIDomain
